import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { CategoryManagementService } from '../../../services/category-management.service';
import {
  FIELD_TYPES, FieldOption, FormField, OPTION_FIELD_TYPES
} from '../../../model/category-management-dto';

/**
 * Editor de un campo del formulario dinámico. Las dependencias se gestionan
 * desde la lista de campos, no desde este editor.
 */
@Component({
  selector: 'app-field-editor-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule
  ],
  templateUrl: './field-editor-dialog.component.html',
  styleUrl: './field-editor-dialog.component.scss'
})
export class FieldEditorDialogComponent implements OnInit {

  form: FormGroup;
  fieldTypes = FIELD_TYPES;
  field: FormField | null;
  errorMessage: string | null = null;

  /** Metadatos de imagen por opción (fileId + url) fuera del FormArray */
  optionImages: { fileId?: string; imageUrl?: string }[] = [];

  uiRoles = [
    { value: 'standalone', label: 'Independiente (standalone)' },
    { value: 'parent', label: 'Padre (tiene dependencias)' },
    { value: 'child', label: 'Hijo (depende de otro)' }
  ];

  uiGroupHints = [
    { value: 'solo', label: 'Ocupa toda la fila (solo)' },
    { value: 'paired', label: 'Media fila (paired)' },
    { value: 'flex', label: 'Flexible (flex)' }
  ];

  constructor(
    private fb: FormBuilder,
    private service: CategoryManagementService,
    private spinner: SpinnerVisibilityService,
    public dialogRef: MatDialogRef<FieldEditorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { field: FormField | null }
  ) {
    this.field = data.field;
  }

  ngOnInit(): void {
    const f = this.field;
    this.form = this.fb.group({
      name: [f?.name || '', [Validators.required, Validators.pattern(/^[a-z][a-z0-9_]*$/)]],
      type: [f?.type || 'input_text', Validators.required],
      fieldTitle: [f?.fieldTitle || '', Validators.required],
      fieldDescription: [f?.fieldDescription || ''],
      required: [f?.required ?? true],
      uiRole: [f?.uiRole || 'standalone'],
      uiGroupHint: [f?.uiGroupHint || 'solo'],
      detailTitle: [f?.detailTitle || ''],
      detailIcon: [f?.detailIcon || ''],
      placeholder: [f?.inputConfig?.placeholder || ''],
      maxLength: [f?.inputConfig?.maxLength ?? null],
      counterMin: [f?.counterConfig?.min ?? 1],
      counterMax: [f?.counterConfig?.max ?? 10],
      confirmText: [f?.confirmText || 'Sí'],
      cancelText: [f?.cancelText || 'No'],
      neutralText: [f?.neutralText || ''],
      options: this.fb.array([])
    });

    (f?.options || []).forEach(option => this.pushOption(option));
  }

  get isEdit(): boolean {
    return !!this.field;
  }

  get typeValue(): string {
    return this.form.get('type')?.value;
  }

  get isInputType(): boolean {
    return this.typeValue === 'input_text' || this.typeValue === 'input_text_area';
  }

  get isCounter(): boolean {
    return this.typeValue === 'counter';
  }

  get isConfirmation(): boolean {
    return this.typeValue === 'confirmation';
  }

  get isPhotos(): boolean {
    return this.typeValue === 'photos';
  }

  get hasOptions(): boolean {
    return OPTION_FIELD_TYPES.includes(this.typeValue);
  }

  get optionsArray(): FormArray {
    return this.form.get('options') as FormArray;
  }

  pushOption(option?: FieldOption): void {
    this.optionsArray.push(this.fb.group({
      option: [option?.option || '', Validators.required]
    }));
    this.optionImages.push({ fileId: option?.fileId, imageUrl: option?.imageUrl });
  }

  removeOption(index: number): void {
    this.optionsArray.removeAt(index);
    this.optionImages.splice(index, 1);
  }

  onOptionImageSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) {
      return;
    }
    const file = input.files[0];
    this.spinner.show();
    this.service.uploadImage(file, 'option-icons').subscribe({
      next: (response) => {
        this.spinner.hide();
        this.optionImages[index] = {
          fileId: response?.data?.fileId,
          imageUrl: response?.data?.url
        };
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.errorMessage = 'Error subiendo la imagen de la opción';
      }
    });
    input.value = '';
  }

  removeOptionImage(index: number): void {
    this.optionImages[index] = {};
  }

  generateName(): void {
    const title: string = this.form.get('fieldTitle')?.value || '';
    const name = title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^([0-9])/, '_$1');
    if (name) {
      this.form.get('name')?.setValue(name);
    }
  }

  save(): void {
    if (this.form.invalid || (this.hasOptions && !this.optionsArray.length)) {
      this.form.markAllAsTouched();
      if (this.hasOptions && !this.optionsArray.length) {
        this.errorMessage = 'Agrega al menos una opción';
      }
      return;
    }

    const value = this.form.value;
    const result: FormField = {
      //Se conservan propiedades existentes que el editor no maneja (ej: dependencies)
      ...(this.field || {}),
      name: value.name,
      type: value.type,
      fieldTitle: value.fieldTitle,
      fieldDescription: value.fieldDescription || undefined,
      required: value.required,
      uiRole: value.uiRole,
      uiGroupHint: value.uiGroupHint,
      detailTitle: value.detailTitle || undefined,
      detailIcon: value.detailIcon || undefined,
      priority: this.field?.priority
    };

    if (this.isInputType) {
      result.inputConfig = {
        placeholder: value.placeholder || undefined,
        maxLength: value.maxLength || undefined
      };
    } else {
      delete result.inputConfig;
    }

    if (this.isCounter) {
      result.counterConfig = { min: value.counterMin, max: value.counterMax };
    } else {
      delete result.counterConfig;
    }

    if (this.isConfirmation) {
      result.confirmText = value.confirmText || 'Sí';
      result.cancelText = value.cancelText || 'No';
      result.neutralText = value.neutralText || undefined;
    } else {
      delete result.confirmText;
      delete result.cancelText;
      delete result.neutralText;
    }

    if (this.hasOptions) {
      result.options = value.options.map((opt: { option: string }, index: number) => {
        const image = this.optionImages[index] || {};
        const option: FieldOption = { option: opt.option };
        if (image.fileId) {
          option.fileId = image.fileId;
        }
        if (image.imageUrl) {
          option.imageUrl = image.imageUrl;
        }
        return option;
      });
    } else {
      delete result.options;
    }

    //Si el tipo ya no soporta dependencias, se descartan
    if (!['confirmation', 'radio', 'select'].includes(result.type) && result.dependencies) {
      result.dependencies = null;
    }

    this.dialogRef.close(result);
  }
}
